import React, {useState, useCallback, useMemo} from 'react';
import RenameDialog from './RenameDialog';

type FileLike = {
   id: string;
   name: string;
};

type Options<T> = {
   files: T[];
   onRename: (file: T, newName: string) => void;
};

export function useRenameDialog<T extends FileLike>({files, onRename}: Options<T>) {
   const [target, setTarget] = useState<T | null>(null);

   type IData = {title: string; onClose?: (target: T) => void};

   const [data, setData] = useState<IData>({
      title: 'Rename',
   });

   const openRename = useCallback((file: T, data: IData = {title: 'Rename'}) => {
      console.debug('open rename', file, data);
      setTarget(file);
      setData(data);
   }, []);

   const closeRename = useCallback(() => {
      setTarget(null);
      data.onClose?.(target!);
   }, [data.onClose, target]);

   const validate = useCallback(
      (name: string) => {
         if (!name.trim()) return 'Name cannot be empty';

         if (
            files.some((f) => f.id !== target?.id && f.name.toLowerCase() === name.toLowerCase())
         ) {
            return 'File with this name already exists';
         }

         return '';
      },
      [files, target]
   );

   const handleSave = useCallback(
      (newName: string) => {
         if (!target) return;
         onRename(target, newName);
         setTarget(null);
      },
      [target, onRename, closeRename]
   );

   const dialog = useMemo(() => {
      if (!target) return null;

      return (
         <RenameDialog
            title={data.title}
            open={Boolean(target)}
            initialValue={target.name}
            validate={validate}
            onClose={closeRename}
            onSave={handleSave}
         />
      );
   }, [target, validate, closeRename, handleSave]);

   return {
      openRename,
      RenameDialogComponent: dialog,
   };
}
